import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { AvailableLanguages, legalBasisOfStayList } from '@constants';
import { PROCESS_TYPE, VISA_TYPE } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { DatepickerHeaderComponent } from '@shared/datepicker-header/datepicker-header.component';
import * as moment from 'moment-timezone';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-long-resi-step4',
  templateUrl: './long-resi-step4.component.html',
  styleUrls: ['./long-resi-step4.component.scss'],
})
export class LongResiStep4Component implements OnInit, AfterViewInit, OnDestroy {
  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  @Input() formGroup: FormGroup;
  @Input() mode: 'foreigner' | 'employee';
  @Output() firstInputShiftTabPressed: EventEmitter<void> = new EventEmitter<void>();
  @Output() lastFieldTabPressed: EventEmitter<void> = new EventEmitter<void>();

  public visitsAmount: number;
  public isVisitsSorting: boolean = false;
  public isVisitsLoading: boolean = false;
  public sortClick$ = new Subject<void>();

  public controls: { [key: string]: FormControl } = {};
  public currentLang: AvailableLanguages;
  public currentDate = moment();
  public pickerHeader = DatepickerHeaderComponent;

  public minValidDate = moment().subtract(125, 'years');
  public maxValidDate = moment().subtract(1, 'day');
  public maxValidFutureDate = moment().subtract(1, 'day').add(30, 'years');

  public maxValidDocumentExpiryDate = moment().add(15, 'years');

  public maxValidLastEntryDate = moment();

  public visaTypes = Object.values(VISA_TYPE);

  public purposeOfStayList = [
    'SETTLE_DOWN',
    PROCESS_TYPE['PURP1_PAID-ACTIVITY'],
    PROCESS_TYPE['PURP2_PAID-ACTIVITY-HIGH-QUALIFICATIONS'],
    PROCESS_TYPE['PURP3_PAID-ACTIVITY-DELEGATED'],
    PROCESS_TYPE['PURP4_BUSINESS-ACTIVITY'],
    PROCESS_TYPE['PURP5_STUDIES'],
    PROCESS_TYPE['PURP10_STAY-WITH-CITIZEN'],
    PROCESS_TYPE['PURP11_STAY-WITH-FOREIGNER'],
    PROCESS_TYPE['PURP16_OTHER'],
  ];

  protected readonly legalBasisOfStayList = legalBasisOfStayList;

  constructor(private readonly translateService: TranslateService) {}

  public ngOnInit(): void {
    const controls: any = {};

    Object.keys(this.formGroup.controls).forEach(fieldName => {
      controls[fieldName] = this.formGroup.controls[fieldName] as FormControl;
    });
    this.controls = controls;

    const lastTravelsOutsidePolandValue = controls.lastTravelsOutsidePoland?.value;
    this.visitsAmount =
      typeof lastTravelsOutsidePolandValue === 'string' && lastTravelsOutsidePolandValue.length
        ? lastTravelsOutsidePolandValue.split(',').length
        : 0;
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.hasAnyFieldAnyValue(this.formGroup.controls)) {
        this.formGroup.markAllAsTouched();
      }

      this.formGroup.controls.currentlyLivingInPoland.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
        this.currentlyLivingInPolandAutoFilling(value);
      });

      this.formGroup.controls.lastTravelsOutsidePoland.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
        if (!value) {
          this.visitsAmount = 0;
          return;
        }

        this.visitsAmount = value.split(',').length;
      });
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public firstInputKeydown($event: KeyboardEvent): void {
    if (!$event.shiftKey) {
      return;
    }

    if ($event.key?.toLowerCase() !== 'tab') {
      return;
    }

    this.firstInputShiftTabPressed.emit();
  }

  public emitLastFieldTabPressed($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();

    this.lastFieldTabPressed.emit();
  }

  public removeLastEntryDate($event: any): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.controls.lastEntryDate.setValue(null);
  }

  private hasAnyFieldAnyValue(controls: { [key: string]: AbstractControl }): boolean {
    return Object.keys(controls).some(controlName => {
      const val = controls[controlName].value;

      if (Array.isArray(val) && val.length > 0) {
        return true;
      }

      return val !== '' && val !== undefined && val !== null && !Array.isArray(val);
    });
  }

  public currentlyLivingInPolandAutoFilling(currentlyLivingInPolandValue: boolean): void {
    const previousVisitsArr = this.formGroup.controls.previousVisitsArr;
    const noPreviousVisits = this.formGroup.controls.noPreviousVisits;

    if (currentlyLivingInPolandValue === true) {
      noPreviousVisits.setValue(false);
      return;
    }
    if (
      currentlyLivingInPolandValue === false &&
      previousVisitsArr?.value?.length === 1 &&
      previousVisitsArr?.value[0]?.leaveDate === 'OBECNIE'
    ) {
      this.formGroup.controls.noPreviousVisits.setValue(true);
      previousVisitsArr.setValue(null);
      return;
    }
  }

  public onVisitsSortingChange(sorting: boolean): void {
    this.isVisitsSorting = sorting;
  }

  public onVisitsLoadingChange(loading: boolean): void {
    this.isVisitsLoading = loading;
  }
}
